import {Control, FieldPath, FieldValues, useWatch} from 'react-hook-form';

import {isString} from 'ramda-adjunct';

import {UserResponseBody} from '@dms/api/shared';

import {RequiredTestIdProps} from 'shared/web';

import {DeletedUserAlert} from './DeletedUserAlert';

interface DeletedUserAlertFieldProps<TFieldValues extends FieldValues> extends RequiredTestIdProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  // The user list the picker was fed, including any persisted assignee re-injected
  // by `useUsersWithPersistedAssignee`.
  users: UserResponseBody[];
}

/**
 * Field-scoped wrapper around `DeletedUserAlert`. It subscribes to a single form
 * field with `useWatch`, so only this component re-renders on change instead of
 * the whole form (which a parent-level `formApi.watch` would trigger). A non-user
 * value (e.g. a `team:<id>` assignee) never matches a user, so no alert renders.
 */
export function DeletedUserAlertField<TFieldValues extends FieldValues>(
  props: DeletedUserAlertFieldProps<TFieldValues>
) {
  const selectedValue = useWatch({control: props.control, name: props.name});

  return (
    <DeletedUserAlert
      users={props.users}
      selectedUserId={isString(selectedValue) ? selectedValue : null}
      data-testid={props['data-testid']}
    />
  );
}
